import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCheck, FiUserPlus, FiEdit3, FiZap, FiCamera, FiInfo } = FiIcons;

export default function Welcome() {
  const { user, profile, signUp, updateProfile, linkDevice, uploadFile } = useAppContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const [authData, setAuthData] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [profileData, setProfileData] = useState({ 
    username: '', 
    title: '', 
    company: '', 
    avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop'
  });
  const [deviceCode, setDeviceCode] = useState('');

  // Auto-advance if user gets authenticated via email link
  useEffect(() => {
    if (user && activeStep === 0) {
      setActiveStep(1);
      setEmailSent(false);
    }
    if (profile?.username && activeStep === 1) {
      setActiveStep(2);
    }
  }, [user, profile, activeStep]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: signUpError } = await signUp(authData.email, authData.password, { 
        firstName: authData.firstName, 
        lastName: authData.lastName 
      });
      if (signUpError) throw signUpError;
      setActiveStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: updateError } = await updateProfile({
        ...profileData,
        first_name: authData.firstName || profile?.first_name,
        last_name: authData.lastName || profile?.last_name
      });
      if (updateError) throw updateError;
      setActiveStep(2);
    } catch (err) {
      setError(err.message || "Username might already be taken.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: linkError } = await linkDevice(deviceCode, undefined);
      if (linkError) throw new Error(typeof linkError === 'string' ? linkError : "Invalid activation code");
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    setLoading(true);
    const { url, error: uploadError } = await uploadFile('profile-assets', file, user.id);
    if (!uploadError) setProfileData(prev => ({ ...prev, avatar_url: url }));
    setLoading(false);
  };

  const steps = [
    { title: "Account", icon: FiUserPlus },
    { title: "Profile", icon: FiEdit3 },
    { title: "Activate", icon: FiZap }
  ];

  const stepVariants = {
    enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 50 : -50, opacity: 0 })
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl w-full">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-100 mb-6">
            <SafeIcon icon={FiZap} className="text-white text-xl" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Welcome to TapBadge</h1>
          <p className="text-gray-500">Complete your setup to start networking smarter.</p>
        </div>

        {/* Progress Bar */}
        <div className="flex justify-between items-center mb-10 relative px-8">
          <div className="absolute left-12 right-12 top-1/2 -translate-y-1/2 h-0.5 bg-gray-200 -z-0">
            <div className="h-full bg-indigo-600 transition-all duration-700" style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }} />
          </div>
          {steps.map((step, idx) => (
            <div key={idx} className="relative z-10 flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${idx <= activeStep ? 'bg-indigo-600 text-white shadow-lg scale-110' : 'bg-white text-gray-400 border-2 border-gray-200'}`}>
                {idx < activeStep ? <SafeIcon icon={FiCheck} size={18} /> : <SafeIcon icon={step.icon} size={18} />}
              </div>
              <span className={`text-[10px] font-bold uppercase mt-2 tracking-wider ${idx <= activeStep ? 'text-indigo-600' : 'text-gray-400'}`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden min-h-[500px] flex flex-col">
          <AnimatePresence mode="wait" custom={activeStep}>
            {activeStep === 0 && !emailSent && (
              <motion.form key="step0" custom={activeStep} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} onSubmit={handleSignUp} className="p-8 sm:p-12 space-y-4 flex-1">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
                  <p className="text-gray-500 text-sm mt-1">Start by securing your digital identity</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input required placeholder="First Name" value={authData.firstName} onChange={e => setAuthData({ ...authData, firstName: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                  <input required placeholder="Last Name" value={authData.lastName} onChange={e => setAuthData({ ...authData, lastName: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <input required type="email" placeholder="john@example.com" value={authData.email} onChange={e => setAuthData({ ...authData, email: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                <input required type="password" placeholder="Password" value={authData.password} onChange={e => setAuthData({ ...authData, password: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                {error && <p className="text-red-500 text-sm p-3 bg-red-50 rounded-lg">{error}</p>}
                <Button type="submit" className="w-full py-4 text-lg rounded-2xl" disabled={loading}>{loading ? "Creating..." : "Next: Design Profile"}</Button>
              </motion.form>
            )}

            {activeStep === 0 && emailSent && (
              <motion.div key="verification" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-12 text-center flex-1 flex flex-col items-center justify-center space-y-6">
                <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center shadow-inner"><SafeIcon icon={FiMail} size={40} /></div>
                <h2 className="text-2xl font-bold text-gray-900">Verify your email</h2>
                <p className="text-gray-600 max-w-xs">We've sent a link to <b>{authData.email}</b>. Please verify your account to continue setup.</p>
                <Button variant="secondary" onClick={() => setEmailSent(false)}>Back to Signup</Button>
              </motion.div>
            )}

            {activeStep === 1 && (
              <motion.form key="step1" custom={activeStep} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} onSubmit={handleProfileUpdate} className="p-8 sm:p-12 space-y-6 flex-1">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900">Customize your card</h2>
                  <div className="relative inline-block group mt-6 mb-2">
                    <img src={profileData.avatar_url} className="w-24 h-24 rounded-full object-cover border-4 border-indigo-50 shadow-md" alt="Avatar" />
                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                      <input type="file" className="hidden" onChange={handleAvatarUpload} />
                      <SafeIcon icon={FiCamera} className="text-white text-xl" />
                    </label>
                  </div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Upload Photo</p>
                </div>
                <div className="space-y-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">tapbadge.net/p/</span>
                    <input required value={profileData.username} onChange={e => setProfileData({ ...profileData, username: e.target.value.toLowerCase().replace(/\s/g, '') })} className="w-full pl-32 pr-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="johndoe" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input placeholder="Job Title" value={profileData.title} onChange={e => setProfileData({ ...profileData, title: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none" />
                    <input placeholder="Company" value={profileData.company} onChange={e => setProfileData({ ...profileData, company: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none" />
                  </div>
                </div>
                {error && <p className="text-red-500 text-sm p-3 bg-red-50 rounded-lg">{error}</p>}
                <Button type="submit" className="w-full py-4 text-lg rounded-2xl shadow-lg" disabled={loading}>{loading ? "Saving..." : "Next: Activate Device"}</Button>
              </motion.form>
            )}

            {activeStep === 2 && (
              <motion.form key="step2" custom={activeStep} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} onSubmit={handleDeviceLink} className="p-8 sm:p-12 space-y-8 flex-1 flex flex-col justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-amber-50 text-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiZap} size={40} /></div>
                  <h2 className="text-2xl font-bold text-gray-900">Activate your badge</h2>
                  <p className="text-gray-500 mt-2 text-sm">Enter the 6-digit code from your card's packaging.</p>
                </div>
                <input required maxLength={6} value={deviceCode} onChange={e => setDeviceCode(e.target.value.toUpperCase())} className="w-full px-4 py-6 border-2 border-dashed border-gray-200 rounded-[2rem] text-center text-4xl font-mono font-bold tracking-[0.4em] focus:border-indigo-500 outline-none uppercase text-indigo-600" placeholder="XXXXXX" />
                {error && <p className="text-red-500 text-sm text-center p-3 bg-red-50 rounded-lg">{error}</p>}
                <div className="space-y-4">
                  <Button type="submit" className="w-full py-5 text-xl rounded-2xl shadow-xl" disabled={loading || deviceCode.length < 6}>{loading ? "Activating..." : "Finish & Activate"}</Button>
                  <button type="button" onClick={() => navigate('/dashboard')} className="w-full text-gray-400 text-xs font-bold uppercase tracking-widest hover:text-indigo-600">Skip for now</button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
